import React from 'react'
import Modal from 'react-bootstrap/Modal'
import Button from 'react-bootstrap/Button'
import Table from 'react-bootstrap/Table'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Card from 'react-bootstrap/Card'
import fileDownload from 'js-file-download'
import I18n from '../../../resources/i18n/I18n'
import '../configuration.scss'
import './ModalImportResults.scss'

const ModalImportResults = ({ show, onClose, results, isDryRun, onProceedToImport, importType }) => {
  if (!results) return null

  const {
    totalProcessed = 0,
    successCount = 0,
    errorCount = 0,
    errors = [],
    created = {},
    skipped = {},
    rawErrors = null,
    allDataAlreadyImported = false,
    shouldReload = false,
    stats = {},
  } = results

  const isTeamImport =
    importType === 'userRelations' ||
    stats.usersLinked !== undefined ||
    stats.supervisorsLinked !== undefined ||
    created.users !== undefined ||
    created.supervisors !== undefined

  const handleClose = () => {
    onClose()
    if (shouldReload) {
      setTimeout(() => {
        window.location.reload()
      }, 200)
    }
  }

  const hasErrors = errorCount > 0
  let title

  if (isDryRun) {
    title = <strong>{I18n.t('ImportSimulationTitle')}</strong>
  } else if (hasErrors) {
    title = I18n.t('ImportCompletedWithErrors')
  } else {
    title = I18n.t('ImportSuccessful')
  }

  if (allDataAlreadyImported && !isDryRun) {
    title = <strong>{I18n.t('ImportDataAlreadyImported')}</strong>
  }

  const downloadErrors = () => {
    if (rawErrors) {
      const filename = isTeamImport ? I18n.t('ImportErrorReportTeams') : I18n.t('ImportErrorReportPatrimonies')
      fileDownload(rawErrors, filename)
    }
  }

  const parseError = (errorLine) => {
    const lineMatch = errorLine.match(/\[Ligne (\d+)\]/)
    const line = lineMatch ? lineMatch[1] : '-'
    const message = errorLine.replace(/\[Ligne \d+\]\s*[-]?\s*/, '')

    return { line, message }
  }

  const parsedErrors = Array.isArray(errors) ? errors.map(parseError) : []

  const hasRealErrors = parsedErrors.some((err) => !err.message.includes('existe déjà') && !err.message.includes('doublon'))

  return (
    <Modal show={show} onHide={handleClose} className="modal-import-results">
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="stats__container">
          <Row>
            <Col md={4}>
              <Card className="stats__card">
                <Card.Body className="stats__card-body">
                  <h3 className="stats__card-number stats__card-number--success">{successCount}</h3>
                  <small className="stats__card-label">{isDryRun ? I18n.t('ImportWillBeImported') : I18n.t('ImportLinesImported')}</small>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="stats__card">
                <Card.Body className="stats__card-body">
                  <h3 className="stats__card-number stats__card-number--error">{errorCount}</h3>
                  <small className="stats__card-label">{I18n.t('ImportLinesWithErrors')}</small>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="stats__card">
                <Card.Body className="stats__card-body">
                  <h3 className="stats__card-number stats__card-number--total">{totalProcessed}</h3>
                  <small className="stats__card-label">{I18n.t('ImportTotalProcessed')}</small>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </div>

        {!isDryRun && successCount > 0 && errorCount > 0 && (
          <div className="alert alert--partial">
            <strong>{I18n.t('ImportPartialCompleted')}</strong>
            <p className="alert__content">
              {I18n.t('ImportPartialMessage', { successCount, errorCount })}
              <br />
              <strong>{I18n.t('ImportPageWillReload')}</strong>
            </p>
          </div>
        )}

        {!isDryRun && successCount > 0 && errorCount === 0 && (
          <div className="alert alert--success">
            <strong>{I18n.t('ImportCompletedSuccess')}</strong>
            <p className="alert__content">{I18n.t('ImportPageWillReload')}</p>
          </div>
        )}

        {!isDryRun && skipped && (skipped.patrimonies > 0 || skipped.sectors > 0 || skipped.agencies > 0 || skipped.lines > 0) && (
          <div className="alert alert--info">
            <strong>{I18n.t('ImportExistingElementsIgnored')}</strong>
            <ul className="alert__list">
              {skipped.lines > 0 && (
                <li>
                  <strong>{I18n.t('ImportCompleteLines', { count: skipped.lines })}</strong>
                </li>
              )}
              {skipped.patrimonies > 0 && <li>{I18n.t('ImportExistingPatrimonies', { count: skipped.patrimonies })}</li>}
              {skipped.sectors > 0 && <li>{I18n.t('ImportExistingSectors', { count: skipped.sectors })}</li>}
              {skipped.agencies > 0 && <li>{I18n.t('ImportExistingAgencies', { count: skipped.agencies })}</li>}
            </ul>
          </div>
        )}

        {allDataAlreadyImported && (
          <div className="alert alert--success">
            <strong>{I18n.t('ImportDataAlreadyImported')}</strong>
            <p className="alert__content">{I18n.t('ImportAllDataImportedMessage')}</p>
          </div>
        )}

        {isDryRun && hasRealErrors && !allDataAlreadyImported && (
          <div className="alert alert--error">
            <strong>{I18n.t('ImportSimulationFailure')}</strong>
            <p className="alert__content">{I18n.t('ImportSimulationFailureMessage')}</p>
          </div>
        )}

        {isDryRun && !hasRealErrors && !allDataAlreadyImported && successCount === 0 && (
          <div className="alert alert--success">
            <strong>{I18n.t('ImportDataAlreadyImported')}</strong>
            <p className="alert__content">{I18n.t('ImportAllDataImportedMessage')}</p>
          </div>
        )}

        {isDryRun && !hasRealErrors && !allDataAlreadyImported && successCount > 0 && (
          <div className={`alert ${errorCount > 0 || (skipped && skipped.lines > 0) ? 'alert--warning' : 'alert--success'}`}>
            <strong>{errorCount > 0 || (skipped && skipped.lines > 0) ? I18n.t('ImportSimulationSuccess') : 'Simulation réussie !'}</strong>
            <p className="alert__content">
              {errorCount > 0 || (skipped && skipped.lines > 0)
                ? `${I18n.t('ImportSimulationWillImport')} ${I18n.t('ImportSimulationDuplicatesIgnored')}`
                : `${successCount} ligne(s) seront importées. Cliquez sur "Procéder à l'import" pour lancer l'import réel.`}
            </p>
            {skipped && skipped.lines > 0 && (
              <p className="alert__secondary-text">{I18n.t('ImportSimulationIgnoredLines', { count: skipped.lines })}</p>
            )}
          </div>
        )}

        {errorCount > 0 && (
          <div className="errors__section">
            <h5 className="mb-3">{I18n.t('ImportErrorsDetails')}</h5>
            <div className="errors__table-container">
              <Table striped bordered hover size="sm">
                <thead className="errors__table-header">
                  <tr>
                    <th width="80">{I18n.t('ImportErrorsLine')}</th>
                    <th>{I18n.t('ImportErrorsError')}</th>
                  </tr>
                </thead>
                <tbody>
                  {parsedErrors.map((error, index) => (
                    <tr key={index}>
                      <td className="errors__line-cell">{error.line}</td>
                      <td className="errors__message-cell">{error.message}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <div className="footer__buttons">
          <Button variant="secondary" className="footer__cancel-btn" onClick={handleClose}>
            {I18n.t('ImportCancel')}
          </Button>

          {rawErrors && errorCount > 0 && (
            <Button className="btn primary footer__button" onClick={downloadErrors}>
              {I18n.t('ImportDownloadErrors')}
            </Button>
          )}

          {isDryRun && onProceedToImport && (
            <Button
              className="btn primary footer__button"
              onClick={onProceedToImport}
              disabled={hasRealErrors || allDataAlreadyImported || successCount === 0}
            >
              {I18n.t('ImportProceed')}
            </Button>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  )
}

export default ModalImportResults
